import { X } from "lucide-react";
import useFocusTrap from "@/hooks/useFocusTrap";

/**
 * The app’s accessible modal surface.
 *
 * Replaces three separate hand-rolled `fixed inset-0` overlays (the apply
 * confirmation, the feedback widget and the admin user panel), none of which
 * had a role, an accessible name, Escape, or a focus trap.
 *
 * `dismissible={false}` is for dialogs that must end in an explicit choice:
 * it removes the close control and ignores backdrop clicks, but deliberately
 * keeps Escape working, because a dialog with no keyboard exit is a trap.
 */
export default function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  dismissible = true,
  testid = "modal",
  className = "",
}) {
  const ref = useFocusTrap(open, onClose);
  if (!open) return null;

  const titleId = `${testid}-title`;
  const descId = description ? `${testid}-description` : undefined;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
      <div
        data-testid={`${testid}-backdrop`}
        aria-hidden="true"
        onClick={dismissible ? onClose : undefined}
        className="absolute inset-0 bg-night/50"
      />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        tabIndex={-1}
        data-testid={testid}
        className={`relative w-full max-w-lg bg-white border border-sand rounded-sm shadow-xl outline-none ${className}`}
      >
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-sand">
          <div>
            <h2 id={titleId} className="font-heading font-bold text-lg tracking-tight text-stone-950">
              {title}
            </h2>
            {description && (
              <p id={descId} className="mt-1 text-sm text-stone-600">{description}</p>
            )}
          </div>
          {dismissible && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close dialog"
              className="shrink-0 p-1.5 -mr-1.5 -mt-1 rounded-sm text-stone-600 hover:text-stone-900 hover:bg-paper-deep"
            >
              <X className="w-4 h-4" aria-hidden="true" />
            </button>
          )}
        </div>

        <div className="px-5 py-4 max-h-[70vh] overflow-y-auto">{children}</div>

        {footer && (
          <div className="px-5 py-4 border-t border-sand flex items-center justify-end gap-2">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
